import type { CollisionWorld, WalkPhysicsConfig } from '@mindcloudxai/shared';
import { FlyMode } from './FlyMode';
import { OrbitMode } from './OrbitMode';
import { AnimMode, AvatarMode } from './PlaceholderMode';
import { WalkMode } from './WalkMode';
import type {
  NavigationManagerOptions,
  NavigationMode,
  NavigationModeContext,
  NavigationModeKey,
} from './types';

export class NavigationManager {
  private modes = new Map<NavigationModeKey, NavigationMode>();
  private current: NavigationMode | null = null;
  private ctx: NavigationModeContext | null = null;

  constructor(options?: NavigationManagerOptions) {
    this.register(new OrbitMode());
    this.register(new FlyMode());
    this.register(new WalkMode());
    this.register(new AvatarMode());
    this.register(new AnimMode());
    if (options) {
      this.init(options);
      if (options.initialMode) this.setMode(options.initialMode);
    }
  }

  register(mode: NavigationMode) {
    this.modes.set(mode.key, mode);
  }

  init(options: NavigationManagerOptions) {
    this.ctx = {
      canvas: options.canvas,
      renderer: options.renderer,
      callbacks: options.callbacks,
    };
    if (options.collisionWorld) this.setCollisionWorld(options.collisionWorld);
    if (options.walkPhysics) this.setWalkPhysics(options.walkPhysics);
  }

  setMode(key: NavigationModeKey) {
    if (this.current?.key === key) return;
    const next = this.modes.get(key);
    if (next && this.ctx) {
      this.current?.deactivate();
      next.activate(this.ctx);
      this.current = next;
      this.ctx.callbacks?.onModeChange?.(key);
    }
  }

  resetCurrentMode(): void {
    if (!this.current || !this.ctx) return;
    const current = this.current;
    current.deactivate();
    current.activate(this.ctx);
  }

  setCollisionWorld(world: CollisionWorld): void {
    const walk = this.modes.get('walk') as WalkMode | undefined;
    walk?.setCollisionWorld(world);
  }

  setWalkPhysics(config: WalkPhysicsConfig): void {
    const walk = this.modes.get('walk') as WalkMode | undefined;
    walk?.setPhysics(config);
  }

  dispose() {
    this.current?.deactivate();
    this.current = null;
    this.ctx = null;
  }
}
